/**
 * Landing Page
 */

import { useEffect, useMemo, useState, useRef } from "react";
import { Instagram, Facebook } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LoggedOutHeader } from "@/components/LoggedOutHeader";
import { PageMeta } from "@/components/seo/PageMeta";
import { faqPageJsonLd } from "@/lib/structuredData";
import { FAQ_ENTRIES } from "@/constants/faqContent";
import { StoreBadges } from "@/components/shared/StoreBadges";
import { FaqDisclosureList } from "@/components/shared/FaqDisclosureList";
import {
  trackMarketingCtaClick,
  type MarketingCtaPlacement,
} from "@/services/marketingCtaAnalytics.service";
import {
  ROOT,
  PRIVACY,
  TERMS,
  LOGIN,
  BLOG,
  BLOG_POST,
  APP_TO_SAVE_ORGANIZE_SOCIAL_POSTS,
  SAVE_PLACES_FROM_TIKTOK,
  ORGANIZE_SAVED_REELS,
  SAVE_TIKTOKS,
  SAVE_RECIPES_FROM_INSTAGRAM_TIKTOK,
  SAVE_TRAVEL_IDEAS_FROM_INSTAGRAM_TIKTOK,
  SAVE_EVENTS_FROM_INSTAGRAM_TIKTOK,
  SAVE_GIFT_IDEAS_FROM_SOCIAL_MEDIA,
  RECIME_ALTERNATIVE,
  STACKLIST_ALTERNATIVE,
} from "@/routes";
import { SUPPORT_LINKS, SOCIAL_LINKS, EXTENSION_LINKS } from "@/constants/links";
import { XLogo, TikTokLogo, getPlatformIcon } from "@/components/shared/PlatformIcon";
import { HowItWorksSection } from "@/components/shared/HowItWorksSection";
import { ExamplesSection } from "@/components/shared/ExamplesSection";
import {
  HERO_STEPS,
  HeroSequence,
  StepIndicators,
  TOUCH_PASSTHROUGH,
  type HeroSequenceHandle,
  type HeroPlaybackState,
} from "@/components/shared/HomepageHero";
import type { Platform } from "@/constants/platforms";

const NAV_LINKS = [{ label: "FAQs", href: "#faq" }];
const SAFARI_EXTENSION_DEMO_GIF = "/videos/stasht-safari-extension-demo.gif";
const SAFARI_EXTENSION_DEMO_ALT = "Demo of Stasht App for Safari saving an Instagram post into Stasht";

const faqs = [
  {
    id: "what-is-stasht",
    question: "What is Stasht?",
    answer:
      "Stasht is like that friend who actually remembers what you\u2019re into. You send us the things you find online \u2014 restaurants, events, products, travel ideas, recipes \u2014 and we organize them, pull out the details, and bring them back to you when they matter.",
  },
  {
    id: "what-platforms",
    question: "What platforms does Stasht work with?",
    answer: (
      <>
        Instagram, X, TikTok, Reddit, YouTube, Pinterest, and the web. You can bulk import existing saves with the{" "}
        <a href={EXTENSION_LINKS.CHROME_EXTENSION} target="_blank" rel="noopener noreferrer" className="url-link">
          Chrome extension
        </a>
        {" "}on desktop or the{" "}
        <a href={EXTENSION_LINKS.SAFARI_EXTENSION} target="_blank" rel="noopener noreferrer" className="url-link">
          Safari extension
        </a>{" "}
        on Mac, iPhone, and iPad.
      </>
    ),
  },
  {
    id: "can-i-import",
    question: "Can I import saves I\u2019ve already made on other apps?",
    answer: (
      <div className="flex flex-col gap-3">
        <span>
          Yes. Stasht can bulk import existing saves with the{" "}
          <a href={EXTENSION_LINKS.CHROME_EXTENSION} target="_blank" rel="noopener noreferrer" className="url-link">
            Chrome extension
          </a>{" "}
          on desktop or the{" "}
          <a href={EXTENSION_LINKS.SAFARI_EXTENSION} target="_blank" rel="noopener noreferrer" className="url-link">
            Safari extension
          </a>{" "}
          on Mac, iPhone, and iPad.
        </span>
        <span>
          In Safari, log into the supported sites you want to import from, then bring those saves into Stasht.
        </span>
        <span>
          You can also sync your X account in{" "}
          <span className="italic font-light text-foreground/60">
            Profile &rsaquo; Account Settings &rsaquo; Connected Accounts
          </span>{" "}
          to pull in all your old bookmarks.
        </span>
        <span>Either way, everything gets automatically organized the moment it lands.</span>
      </div>
    ),
  },
  {
    id: "what-devices",
    question: "What devices can I use Stasht on?",
    answer: "iPhone, iPad, Android, and desktop. Your stash syncs across all of them.",
  },
  {
    id: "how-different-from-ig",
    question: "How is this different from saving posts on Instagram?",
    answer:
      "Instagram saves stay on Instagram. They don\u2019t tell you what\u2019s inside, they don\u2019t organize themselves, and they don\u2019t come back to you. Stasht works across every platform and does the work your saves were never going to do on their own.",
  },
  {
    id: "is-stasht-free",
    question: "Is Stasht free?",
    answer:
      "Yes. We\u2019re early and focused on building the best experience we can. Just download and start stashing.",
  },
  {
    id: "businesses-creators",
    question: "Can businesses or content creators use Stasht?",
    answer: (
      <>
        We&apos;re building for that. If you&apos;re interested, reach out at{" "}
        <a href="mailto:business@stasht.app" className="url-link">
          business@stasht.app
        </a>
        .
      </>
    ),
  },
  {
    id: "still-have-questions",
    question: "Still have questions?",
    answer: (
      <>
        <a href="mailto:support@stasht.app" className="url-link">
          support@stasht.app
        </a>
      </>
    ),
  },
];

// Structured data mirrors the faqs array above (plain-text source: faqContent.ts)
const LANDING_JSON_LD = [faqPageJsonLd(FAQ_ENTRIES)];

const FEATURED_GUIDES = [
  {
    title: "How to save TikTok restaurants to a map",
    description: "Turn restaurant videos into places with names, addresses, source links, and map recall.",
    to: BLOG_POST({ slug: "how-to-save-tiktok-restaurants-to-a-map" }),
  },
  {
    title: "Where to find saved Instagram Reels",
    description: "Open Profile > menu > Saved, then check All posts, collections, likes, and missing-post reasons.",
    to: BLOG_POST({ slug: "where-to-find-saved-instagram-reels" }),
  },
  {
    title: "How to find your saved TikToks",
    description: "Where TikTok saves live, why they get hard to search, and how to build a better find-it-later habit.",
    to: BLOG_POST({ slug: "how-to-find-your-saved-tiktoks" }),
  },
  {
    title: "The best apps for saving places from TikTok",
    description: "A practical comparison for people turning restaurants, bars, and trip ideas into places they can use.",
    to: BLOG_POST({ slug: "best-apps-to-save-places-from-tiktok" }),
  },
];

const EXPLORE_LINKS = [
  { label: "Save social media posts", to: APP_TO_SAVE_ORGANIZE_SOCIAL_POSTS() },
  { label: "Save places from TikTok", to: SAVE_PLACES_FROM_TIKTOK() },
  { label: "Organize saved Reels", to: ORGANIZE_SAVED_REELS() },
  { label: "Save TikToks", to: SAVE_TIKTOKS() },
  { label: "Save recipes", to: SAVE_RECIPES_FROM_INSTAGRAM_TIKTOK() },
  { label: "Save travel ideas", to: SAVE_TRAVEL_IDEAS_FROM_INSTAGRAM_TIKTOK() },
  { label: "Save events", to: SAVE_EVENTS_FROM_INSTAGRAM_TIKTOK() },
  { label: "Save gift ideas", to: SAVE_GIFT_IDEAS_FROM_SOCIAL_MEDIA() },
  { label: "ReciMe alternative", to: RECIME_ALTERNATIVE() },
  { label: "Stacklist alternative", to: STACKLIST_ALTERNATIVE() },
];

const ChromeExtensionCta = () => (
  <a
    href={EXTENSION_LINKS.CHROME_EXTENSION}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Get the Chrome extension"
    title="Get the Chrome extension"
    className="inline-flex w-fit items-center gap-3 rounded-[10px] border-[1.5px] border-foreground/20 bg-background px-5 py-2.5 font-semibold text-foreground text-base tracking-[-0.45px] leading-caption transition hover:border-foreground/40 active:scale-[0.98] md:rounded-xl md:px-6 md:py-3 md:text-lg"
  >
    <img
      src="https://www.google.com/chrome/static/images/chrome-logo-m100.svg"
      alt="Chrome browser logo"
      className="h-6 w-6"
    />
    <span>Chrome</span>
  </a>
);

const SafariExtensionCta = () => (
  <a
    href={EXTENSION_LINKS.SAFARI_EXTENSION}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Get Stasht for Safari"
    title="Get Stasht for Safari"
    className="inline-flex w-fit items-center gap-3 rounded-[10px] border-[1.5px] border-foreground/20 bg-background px-5 py-2.5 font-semibold text-foreground text-base tracking-[-0.45px] leading-caption transition hover:border-foreground/40 active:scale-[0.98] md:rounded-xl md:px-6 md:py-3 md:text-lg"
  >
    <img
      src="https://developer.apple.com/assets/elements/icons/safari/safari-96x96_2x.png"
      alt="Safari browser logo"
      className="h-6 w-6"
    />
    <span>Safari</span>
  </a>
);

type ExtensionCtaGroupProps = {
  align?: "start" | "center";
  labelClassName?: string;
};

const ExtensionCtaGroup = ({ align = "start", labelClassName = "text-sm" }: ExtensionCtaGroupProps) => {
  const containerAlign = align === "center" ? "items-center" : "items-start";
  const rowJustify = align === "center" ? "justify-center" : "justify-start";

  return (
    <div className={`flex flex-col gap-3 ${containerAlign}`}>
      <p className={`${labelClassName} font-semibold text-foreground/70 m-0`}>Get the extensions</p>
      <div className={`flex flex-wrap gap-3 ${rowJustify}`}>
        <ChromeExtensionCta />
        <SafariExtensionCta />
      </div>
    </div>
  );
};

const HeroCaption = ({ active, paused }: { active: number; paused: boolean }) => (
  <div
    className="grid w-full max-w-[600px] text-center lg:text-left"
    aria-live={paused ? "polite" : "off"}
    aria-atomic="true"
  >
    {HERO_STEPS.map((step, index) => (
      <div
        key={index}
        className={`col-start-1 row-start-1 ${index === active ? "visible" : "invisible"}`}
        aria-hidden={index !== active}
      >
        <h2 className="text-xl sm:text-2xl font-semibold leading-tight text-foreground">
          {step.headingLine1} <span className="text-brand-700 dark:text-brand-400">{step.headingLine2}</span>
        </h2>
        <p className="mt-2 text-sm sm:text-base leading-relaxed text-foreground/70">
          {step.subtitleLine1} {step.subtitleLine2}
        </p>
      </div>
    ))}
  </div>
);

const WebLanding = () => {
  const [heroStep, setHeroStep] = useState(0);
  const [heroPlayback, setHeroPlayback] = useState<HeroPlaybackState>({
    paused: false,
    manuallyPaused: false,
    reducedMotion: false,
  });
  const heroPaused = heroPlayback.paused;
  const heroControlsRef = useRef<HeroSequenceHandle | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const inviteParam = useMemo(() => new URLSearchParams(location.search).get("invite")?.trim(), [location.search]);
  const signupUrl = LOGIN();
  const loginUrl = inviteParam ? `${LOGIN()}?invite=${encodeURIComponent(inviteParam)}` : LOGIN();
  const trackSignupLoginCta = (placement: MarketingCtaPlacement, label: string) => {
    trackMarketingCtaClick({
      destination: "signup_login",
      surface: "homepage",
      placement,
      label,
      href: signupUrl,
    });
  };

  useEffect(() => {
    if (location.hash === "#waitlist") {
      navigate(signupUrl, { replace: true });
    }
  }, [location.hash, navigate, signupUrl]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PageMeta
        title="Stasht: Save & Organize Bookmarks, Screenshots & Links"
        description="Save and organize social media posts from Instagram, TikTok, X, YouTube, and the web. Stasht turns saves into maps, calendars, reminders, and searchable collections."
        path="/"
        jsonLd={LANDING_JSON_LD}
      />
      {/* Navigation */}
      <LoggedOutHeader logoLink={ROOT()} navLinks={NAV_LINKS} loginHref={loginUrl} surface="homepage" />

      <main className="flex-1 overflow-x-clip">
        {/* Hero Section */}
        <section className="flex gap-8 md:gap-12 lg:gap-16 items-start justify-center pt-6 pb-3 md:py-10 lg:py-14 relative w-full overflow-visible">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat dark:hidden"
            style={{ backgroundImage: "url('/stasht-hero-background.png')" }}
          />
          <div className="page-frame">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12 lg:gap-16 items-center pb-3 md:pb-10 lg:pb-14 pt-4 md:pt-6 lg:pt-8 px-0 relative w-full overflow-visible">
              {/* Header text - mobile only */}
              <div
                className={`flex flex-col gap-3 items-center justify-center min-w-0 relative z-10 order-1 animate-slide-in-left md:animate-slide-in-left lg:hidden ${TOUCH_PASSTHROUGH}`}
              >
                <h1 className="font-semibold leading-display text-display-36 sm:text-display-44 md:text-display-52 tracking-[-1.08px] sm:tracking-[-1.32px] md:tracking-[-1.56px] w-full text-center text-foreground">
                  <span className="block">Save &amp; organize </span>
                  <span className="block text-brand-700 dark:text-brand-400">bookmarks, screenshots &amp; links.</span>
                </h1>
                <p
                  className="font-normal leading-normal max-w-[567px] relative text-sm sm:text-base text-foreground w-full text-center"
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  Save posts, screenshots, and links from Instagram, TikTok, and the web. Stasht pulls out the details
                  and brings them back on your map, calendar, reminders, and search.
                </p>
              </div>

              {/* Image - shows second on mobile, right column on desktop */}
              <div className="relative w-full max-w-[480px] aspect-[5/6] flex-none lg:max-w-none lg:aspect-auto lg:w-auto lg:flex-1 lg:h-[640px] min-h-0 min-w-0 overflow-visible order-2 animate-slide-in-right md:animate-slide-in-right">
                <HeroSequence
                  onStepChange={setHeroStep}
                  onPlaybackStateChange={setHeroPlayback}
                  controlsRef={heroControlsRef}
                />
              </div>

              {/* Combined content container for desktop - hidden on mobile */}
              <div className="hidden lg:flex flex-col flex-1 gap-6 md:gap-8 lg:gap-10 items-start justify-center min-h-0 min-w-0 relative order-1 animate-slide-in-left md:animate-slide-in-left">
                <div className="flex flex-col gap-4 md:gap-6 lg:gap-8 items-start justify-center relative w-full whitespace-pre-wrap">
                  <div
                    role="heading"
                    aria-level={1}
                    className="font-semibold leading-display text-display-28 sm:text-display-40 md:text-display-52 lg:text-display-64 tracking-[-0.84px] sm:tracking-[-1.2px] md:tracking-[-1.56px] lg:tracking-[-1.92px] w-full text-foreground"
                  >
                    <span className="block">Save &amp; organize </span>
                    <span className="block text-brand-700 dark:text-brand-400">bookmarks, screenshots &amp; links.</span>
                  </div>
                  <p
                    className="font-normal leading-normal max-w-[600px] text-base md:text-lg text-foreground w-full"
                    style={{ fontVariationSettings: "'opsz' 14" }}
                  >
                    Save posts, screenshots, and links from Instagram, TikTok, and the web. Stasht pulls out the details
                    and brings them back on your map, calendar, reminders, and search.
                  </p>
                </div>
                <div className="flex flex-col gap-3 w-full items-start">
                  <HeroCaption active={heroStep} paused={heroPaused} />
                  <StepIndicators
                    active={heroStep}
                    total={HERO_STEPS.length}
                    goTo={i => heroControlsRef.current?.goTo(i, true)}
                    paused={heroPaused}
                    reducedMotion={heroPlayback.reducedMotion}
                    onPause={() => heroControlsRef.current?.pause()}
                    onTogglePlayback={() => {
                      if (heroPlayback.paused) heroControlsRef.current?.resume();
                      else heroControlsRef.current?.pause();
                    }}
                  />
                </div>
                <div className="flex flex-col gap-0 relative w-full">
                  <div className="flex flex-wrap gap-3 items-center">
                    <StoreBadges surface="homepage" placement="hero" />
                  </div>
                  <div className="mt-4">
                    <ExtensionCtaGroup />
                  </div>
                  <p className="text-sm text-foreground/50 mt-1" style={{ fontVariationSettings: "'opsz' 14" }}>
                    Free on iOS, Android, and desktop.
                  </p>
                </div>
              </div>

              {/* CTA - mobile only */}
              <div
                className={`flex flex-col gap-3 w-full max-w-[480px] items-center justify-center min-w-0 relative z-10 order-3 lg:hidden ${TOUCH_PASSTHROUGH}`}
              >
                <HeroCaption active={heroStep} paused={heroPaused} />
                <StepIndicators
                  active={heroStep}
                  total={HERO_STEPS.length}
                  goTo={i => heroControlsRef.current?.goTo(i, true)}
                  paused={heroPaused}
                  reducedMotion={heroPlayback.reducedMotion}
                  onPause={() => heroControlsRef.current?.pause()}
                  onTogglePlayback={() => {
                    if (heroPlayback.paused) heroControlsRef.current?.resume();
                    else heroControlsRef.current?.pause();
                  }}
                />
                <div className="mt-5 flex flex-col items-center gap-2">
                  <div className="flex flex-row items-center gap-3">
                    <StoreBadges className="h-12 sm:h-14" surface="homepage" placement="hero" />
                  </div>
                  <div className="mt-2">
                    <ExtensionCtaGroup align="center" labelClassName="text-xs" />
                  </div>
                  <p className="text-xs text-foreground/50" style={{ fontVariationSettings: "'opsz' 14" }}>
                    Free on iOS, Android, and desktop.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section - hidden for now */}

        {/* How It Works Section - hidden for now */}

        {/* Social Proof Section - hidden for now */}

        {/* How It Works Section */}
        <HowItWorksSection />

        {/* Examples Section */}
        <ExamplesSection />

        {/* Guides Section */}
        <section className="flex items-start justify-center py-10 md:py-20 lg:py-24 relative w-full bg-brand-500/[0.06]">
          <div className="page-frame">
            <div className="flex flex-col gap-8 md:gap-10">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div className="flex flex-col gap-3">
                  <h2 className="font-semibold leading-none text-display-28 sm:text-display-40 md:text-display-52 lg:text-display-64 tracking-[-0.84px] sm:tracking-[-1.2px] md:tracking-[-1.56px] lg:tracking-[-1.92px] text-foreground">
                    Guides for saves <span className="text-brand-700 dark:text-brand-400">worth finding</span>
                  </h2>
                  <p
                    className="font-normal leading-normal max-w-[640px] text-base md:text-lg text-foreground/70"
                    style={{ fontVariationSettings: "'opsz' 14" }}
                  >
                    Practical notes on organizing saved Reels, TikToks, restaurants, and the rest of the internet pile.
                  </p>
                </div>
                <Link
                  to={BLOG()}
                  className="font-semibold text-brand-700 text-base md:text-lg hover:underline dark:text-brand-400"
                >
                  Read the blog
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {FEATURED_GUIDES.map(guide => (
                  <Link
                    key={guide.to}
                    to={guide.to}
                    className="rounded-2xl border border-foreground/10 bg-background p-5 md:p-6 transition hover:border-brand-500/40"
                  >
                    <h3 className="text-lg md:text-xl font-semibold text-foreground leading-tight m-0">
                      {guide.title}
                    </h3>
                    <p className="text-sm md:text-base text-foreground/70 leading-normal mt-3 mb-0">
                      {guide.description}
                    </p>
                  </Link>
                ))}
              </div>
              <div className="flex flex-col gap-3">
                <p className="text-sm font-semibold uppercase tracking-[0.08em] text-foreground/50 m-0">
                  Popular ways to use Stasht
                </p>
                <div className="flex flex-wrap gap-2">
                  {EXPLORE_LINKS.map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="rounded-full border border-foreground/10 bg-background px-3 py-1.5 text-sm font-semibold text-foreground transition hover:border-brand-500/40 hover:text-brand-700 dark:hover:text-brand-400"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bulk Import Section */}
        <section className="flex items-start justify-center py-10 md:py-20 lg:py-28 relative w-full">
          <div className="page-frame">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
              {/* Left - Copy */}
              <div className="flex flex-col gap-4 md:gap-6">
                <h2 className="font-semibold leading-none text-display-28 sm:text-display-40 md:text-display-52 lg:text-display-64 tracking-[-0.84px] sm:tracking-[-1.2px] md:tracking-[-1.56px] lg:tracking-[-1.92px] text-foreground">
                  Already saving on <span className="text-brand-700 dark:text-brand-400">other apps?</span>
                </h2>
                <p
                  className="font-normal leading-normal max-w-[567px] text-base md:text-lg text-foreground/70"
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  Everything you've saved on Instagram, X, TikTok, Reddit, YouTube, Pinterest, and the web — imported in
                  one click and automatically organized.
                </p>
                <p
                  className="font-normal leading-normal max-w-[567px] text-sm md:text-base text-foreground/70"
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  Use Chrome on desktop or Safari on Mac, iPhone, and iPad to bulk import old saves. In Safari, log into
                  supported sites to import, or send pages and posts into your stash while you browse.
                </p>
                <div className="mt-2">
                  <ExtensionCtaGroup />
                </div>
              </div>
              {/* Right - Safari extension demo */}
              <div className="flex flex-col gap-5 items-center md:items-end">
                <a
                  href={EXTENSION_LINKS.SAFARI_EXTENSION}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get Stasht App for Safari"
                  className="block w-full max-w-[300px] sm:max-w-[340px] md:max-w-[320px] lg:max-w-[360px]"
                >
                  <img
                    src={SAFARI_EXTENSION_DEMO_GIF}
                    alt={SAFARI_EXTENSION_DEMO_ALT}
                    loading="lazy"
                    className="aspect-[800/1422] w-full rounded-2xl border border-foreground/10 object-cover shadow-[0_24px_60px_rgba(15,23,42,0.16)]"
                  />
                </a>
                <div className="flex flex-wrap gap-3 justify-center md:justify-end items-center">
                  {(["instagram", "twitter", "tiktok", "reddit", "youtube", "pinterest", "web"] as Platform[]).map(p => (
                    <div
                      key={p}
                      className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-muted/50 flex items-center justify-center"
                    >
                      {getPlatformIcon(p, 24)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile CTA + Payoff Section */}
        <section className="flex flex-col items-center justify-center py-10 md:py-20 lg:py-28 w-full gap-8">
          <div className="page-frame text-center">
            <h2 className="text-display-32 sm:text-display-48 md:text-display-64 lg:text-display-80 font-bold tracking-[-0.96px] sm:tracking-[-1.44px] md:tracking-[-1.92px] lg:tracking-[-2.4px] text-foreground leading-display">
              Finally, saves <span className="text-brand-700 dark:text-brand-400">that work.</span>
            </h2>
          </div>
          <div className="flex flex-col items-center gap-3 lg:hidden">
            <div className="flex flex-row gap-3 items-center">
              <StoreBadges className="h-14 sm:h-16" surface="homepage" placement="body" />
            </div>
            <p className="text-xs text-foreground/50" style={{ fontVariationSettings: "'opsz' 14" }}>
              Free on iOS, Android, and desktop.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section
          id="faq"
          className="flex items-start justify-center py-10 md:py-20 lg:py-28 relative w-full overflow-visible"
        >
          <div className="page-frame">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-8 md:gap-12 lg:gap-16 items-start">
              {/* Left Column - Headline and Subhead */}
              <div className="flex flex-col gap-4 md:gap-6 lg:gap-8 items-start justify-center relative w-full">
                <h2 className="flex flex-col gap-1 md:gap-2 lg:gap-3 font-semibold items-start leading-none relative text-display-28 sm:text-display-40 md:text-display-52 lg:text-display-64 tracking-[-0.84px] sm:tracking-[-1.2px] md:tracking-[-1.56px] lg:tracking-[-1.92px] w-full">
                  <span className="relative shrink-0 text-foreground w-full">Frequently Asked</span>
                  <span className="relative shrink-0 text-brand-700 w-full dark:text-brand-400">Questions</span>
                </h2>
                <p
                  className="font-normal leading-normal max-w-[567px] relative text-base md:text-lg text-foreground w-full"
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  New app, lots of questions. Here are the ones we hear most.
                </p>
              </div>

              {/* Right Column - Accordion */}
              <div className="flex flex-col gap-0 items-start justify-center relative w-full">
                <FaqDisclosureList
                  name="homepage-faq"
                  items={faqs}
                  itemClassName="border-foreground/10 last:border-none"
                  questionClassName="text-foreground font-semibold text-base md:text-lg"
                  chevronClassName="text-foreground group-open:text-brand-500"
                  answerClassName="text-foreground/70 text-base md:text-lg font-normal leading-normal"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="bg-brand-500/[0.06] flex items-center justify-center py-10 md:py-20 lg:py-28 w-full">
          <div className="page-frame text-center">
            <h2 className="text-display-24 sm:text-display-40 md:text-display-52 lg:text-display-64 font-bold tracking-[-0.72px] sm:tracking-[-1.2px] md:tracking-[-1.56px] lg:tracking-[-1.92px] text-foreground leading-display max-w-[900px] mx-auto">
              So go ahead. Stash it. <span className="text-brand-700 whitespace-nowrap dark:text-brand-400">Make your saves worth it.</span>
            </h2>
            <div className="flex flex-col items-center gap-4 mt-8 md:mt-10">
              <Link
                to={signupUrl}
                className="rounded-full border-[1.5px] border-foreground bg-foreground px-5 md:px-6 py-2.5 md:py-3 font-semibold text-background text-base md:text-lg tracking-[-0.45px] leading-caption transition active:bg-foreground/80 active:border-foreground/80 active:scale-[0.98]"
                onClick={() => trackSignupLoginCta("final_cta", "Get Stasht")}
              >
                Get Stasht
              </Link>
              <p
                className="text-sm md:text-base font-normal text-foreground/50"
                style={{ fontVariationSettings: "'opsz' 14" }}
              >
                Free on iOS, Android, and desktop.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer 3 */}
      <footer className="bg-background flex flex-col items-center overflow-hidden relative w-full">
        {/* Top Section */}
        <div className="flex flex-col md:flex-row gap-8 md:gap-4 items-center md:items-start py-10 md:py-20 lg:py-28 relative shrink-0 page-frame">
          {/* Logo Column */}
          <div className="flex flex-col items-center md:items-start shrink-0 w-full md:w-[324px]">
            <img alt="Stasht logo" src="/Stasht Full Logo.png" className="h-14 w-auto md:h-20" />
            <p
              className="hidden md:block text-sm md:text-base font-normal text-foreground/70 mt-3"
              style={{ fontVariationSettings: "'opsz' 14" }}
            >
              We&apos;re just getting started.
            </p>
          </div>

          {/* Links + Social */}
          <div className="flex flex-col items-center md:items-start gap-6 md:gap-0 flex-1 w-full">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-6 md:gap-8 lg:gap-12 w-full">
              <Link
                to={signupUrl}
                className="font-semibold text-foreground text-lg md:text-2xl lg:text-display-32 leading-caption transition"
                onClick={() => trackSignupLoginCta("footer", "Get started")}
              >
                Get started
              </Link>
              <Link
                to={BLOG()}
                className="font-semibold text-foreground text-lg md:text-2xl lg:text-display-32 leading-caption transition"
              >
                Blog
              </Link>
              <a
                href="#faq"
                className="font-semibold text-foreground text-lg md:text-2xl lg:text-display-32 leading-caption transition"
              >
                FAQ
              </a>
              <a
                href={SUPPORT_LINKS.FEATUREBASE_PORTAL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground text-lg md:text-2xl lg:text-display-32 leading-caption transition"
              >
                What&apos;s new?
              </a>
              <a
                href={SUPPORT_LINKS.FEATUREBASE_PORTAL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-foreground text-lg md:text-2xl lg:text-display-32 leading-caption transition"
              >
                Feedback
              </a>
            </div>
            <div className="flex flex-col items-center md:items-start gap-3 mt-0 md:mt-6">
              <p className="font-semibold text-foreground text-sm md:text-base tracking-[-0.45px]">Follow us</p>
              <div className="flex gap-4 items-center">
                <a
                  href={SOCIAL_LINKS.INSTAGRAM}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center size-6 text-foreground transition"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram size={24} />
                </a>
                <a
                  href={SOCIAL_LINKS.TWITTER}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center size-6 transition"
                  aria-label="Follow us on X"
                >
                  <XLogo size={24} />
                </a>
                <a
                  href={SOCIAL_LINKS.TIKTOK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center size-6 text-foreground transition"
                  aria-label="Follow us on TikTok"
                >
                  <TikTokLogo size={24} />
                </a>
                <a
                  href={SOCIAL_LINKS.FACEBOOK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center size-6 text-foreground transition"
                  aria-label="Follow us on Facebook"
                >
                  <Facebook size={24} />
                </a>
              </div>
              <p
                className="md:hidden text-sm font-normal text-foreground/70 mt-4"
                style={{ fontVariationSettings: "'opsz' 14" }}
              >
                We&apos;re just getting started.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-foreground border-t-[1.5px] relative shrink-0 w-full">
          <div className="flex flex-col md:flex-row items-start justify-between overflow-hidden py-6 md:py-8 lg:py-10 relative rounded-[inherit] page-frame gap-4 md:gap-0">
            <div className="flex flex-col md:flex-row flex-1 items-center md:items-center justify-between min-h-0 min-w-0 overflow-hidden px-0 relative shrink-0 gap-4 md:gap-0 w-full md:w-auto">
              <p className="font-semibold leading-normal relative shrink-0 text-foreground text-sm md:text-base text-center md:text-left">
                © {new Date().getFullYear()} Copyright. All Rights Reserved.
              </p>
              <div className="flex font-semibold gap-3 md:gap-4 items-center leading-normal relative shrink-0 text-foreground text-sm md:text-base">
                <Link to={PRIVACY()} className="relative transition">
                  Privacy Policy
                </Link>
                <Link to={TERMS()} className="relative transition">
                  Terms & Conditions
                </Link>
              </div>
              <div className="flex flex-col gap-2 items-center md:items-start relative shrink-0">
                <div className="flex gap-4 md:gap-6 items-center justify-center md:justify-start">
                  <StoreBadges className="h-10" surface="homepage" placement="footer" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WebLanding;
